import { Animal } from './creatures/animal.js';
import { isSpecies } from './creatures/catalog.js';
import { isFootBlocked } from './movement.js';
import { Npc, DIRECTIONS as NPC_DIRECTIONS } from './npc/npc.js';
import { MAP_CHANNELS } from './tileset-source.js';

export const CHUNK_SIZE = 32;
const CELLS_PER_CHUNK = CHUNK_SIZE * CHUNK_SIZE;

// Chunk coordinates pack into one number so the hot tile-query path allocates
// nothing (string keys cost a template string per lookup, and world queries
// run tens of thousands of times per frame). ±0x8000 chunks ≈ ±1M tiles.
const CHUNK_KEY_OFFSET = 0x8000;
const chunkKeyFor = (chunkX, chunkY) => (chunkX + CHUNK_KEY_OFFSET) * 0x10000 + (chunkY + CHUNK_KEY_OFFSET);
const chunkXOf = (key) => Math.floor(key / 0x10000) - CHUNK_KEY_OFFSET;
const chunkYOf = (key) => (key % 0x10000) - CHUNK_KEY_OFFSET;

const TRANSITION_DIRECTIONS = [
  { dx: 0, dy: -1, mask: 1 },
  { dx: 1, dy: 0, mask: 2 },
  { dx: 0, dy: 1, mask: 4 },
  { dx: -1, dy: 0, mask: 8 },
];
const MASK_VECTOR = { 1: [0, -1], 2: [1, 0], 4: [0, 1], 8: [-1, 0] };
const OPPOSITE_MASK = { 1: 4, 2: 8, 4: 1, 8: 2 };
// Carving a step lowers terrain, and terrain-following art (foundation, ground
// surfaces, outdoor floors) rides down with it. Anything built on the cell would
// instead be left standing in the middle of the stairwell.
const STRUCTURAL_CHANNELS = ['exterior', 'interiorFloor', 'onGround', 'treeWood', 'wall', 'wallObject', 'roof'];

class Chunk {
  constructor() {
    this.layers = Object.fromEntries(MAP_CHANNELS.map((layer) => [layer, new Int32Array(CELLS_PER_CHUNK)]));
    this.placements = Object.fromEntries(MAP_CHANNELS.map((layer) => [layer, new Int32Array(CELLS_PER_CHUNK)]));
    this.collision = new Int8Array(CELLS_PER_CHUNK);
    this.elevation = new Uint8Array(CELLS_PER_CHUNK);
    this.transitions = new Uint8Array(CELLS_PER_CHUNK);
    this.collision.fill(-1);
    this.occupiedLayerCells = 0;
    this.collisionOverrides = 0;
    this.elevatedCells = 0;
    this.transitionCells = 0;
  }

  get empty() {
    return this.occupiedLayerCells === 0 && this.collisionOverrides === 0
      && this.elevatedCells === 0 && this.transitionCells === 0;
  }
}

/** Sparse, unbounded tile map backed by compact 32x32 chunks. */
export class World {
  constructor(tileCatalog) {
    this.tileCatalog = tileCatalog instanceof Map
      ? tileCatalog
      : new Map(tileCatalog.map((tile) => [tile.id, tile]));
    /** @type {Map<string, Chunk>} */
    this.chunks = new Map();
    /** @type {Map<number, { x: number, y: number, layers: string[] }>} */
    this.placementIndex = new Map();
    this.nextPlacementId = 1;
    /** @type {import('./creatures/animal.js').Animal[]} */
    this.creatures = [];
    this.nextCreatureId = 1;
    /** @type {import('./npc/npc.js').Npc[]} */
    this.npcs = [];
  }

  serialize() {
    const placements = [];
    for (const placement of this.placementIndex.values()) {
      const primaryLayer = placement.layers?.[0];
      if (!primaryLayer) continue;
      const tileId = this.getLayer(placement.x, placement.y, primaryLayer);
      if (tileId) placements.push({ x: placement.x, y: placement.y, tileId });
    }

    const cells = [];
    for (const [key, chunk] of this.chunks) {
      // A chunk holding only placements has no cell data to scan.
      if (!chunk.collisionOverrides && !chunk.elevatedCells && !chunk.transitionCells) continue;
      const chunkX = chunkXOf(key);
      const chunkY = chunkYOf(key);
      for (let index = 0; index < CELLS_PER_CHUNK; index += 1) {
        const collision = chunk.collision[index];
        const elevation = chunk.elevation[index];
        const transition = chunk.transitions[index];
        if (collision === -1 && elevation === 0 && transition === 0) continue;
        cells.push({
          x: chunkX * CHUNK_SIZE + index % CHUNK_SIZE,
          y: chunkY * CHUNK_SIZE + Math.floor(index / CHUNK_SIZE),
          ...(collision !== -1 ? { collision } : {}),
          ...(elevation ? { elevation } : {}),
          ...(transition ? { transition } : {}),
        });
      }
    }
    return {
      version: 1,
      placements,
      cells,
      creatures: this.creatures.map((creature) => creature.toJSON()),
      npcs: this.npcs.map((npc) => npc.toJSON()),
    };
  }

  /**
   * Rebuild a world from serialized data, dropping whatever no longer fits.
   * Saves keep authoring order, but dependencies can point the other way after
   * edits: an interior wall needs its shell, a roof its footprint, a floor its
   * foundation. Rather than hardcoding a channel order, rejected placements are
   * retried until a pass makes no progress — whatever still fails is genuinely
   * incompatible. Returns the world plus counts of everything dropped.
   */
  static restore(tileCatalog, data) {
    if (!data || data.version !== 1 || !Array.isArray(data.placements) || !Array.isArray(data.cells)
      || (data.creatures !== undefined && !Array.isArray(data.creatures))
      || (data.npcs !== undefined && !Array.isArray(data.npcs))) {
      throw new Error('Unsupported or invalid saved world data');
    }
    const world = new World(tileCatalog);
    let removedTiles = 0;
    let pending = [];
    for (const placement of data.placements) {
      if (!placement || !Number.isInteger(placement.x) || !Number.isInteger(placement.y)
        || !Number.isInteger(placement.tileId) || !world.tileCatalog.has(placement.tileId)) removedTiles++;
      else pending.push(placement);
    }
    let progressed = true;
    while (pending.length && progressed) {
      progressed = false;
      const deferred = [];
      for (const placement of pending) {
        if (world.placeTile(placement.x, placement.y, placement.tileId)) progressed = true;
        else deferred.push(placement);
      }
      pending = deferred;
    }
    removedTiles += pending.length;

    let removedCells = 0;
    const transitionCells = [];
    for (const cell of data.cells) {
      if (!cell || !Number.isInteger(cell.x) || !Number.isInteger(cell.y)
        || (cell.elevation !== undefined && (!Number.isInteger(cell.elevation) || cell.elevation < 1 || cell.elevation > 3))
        || (cell.transition !== undefined && (!Number.isInteger(cell.transition) || cell.transition < 1 || cell.transition > 8))
        || (cell.collision !== undefined && ![0, 1].includes(cell.collision))) {
        throw new Error('Saved world contains invalid cell data');
      }
      if (cell.elevation && !world.setElevation(cell.x, cell.y, cell.elevation)) removedCells++;
      if (cell.transition) transitionCells.push(cell);
      if (cell.collision !== undefined) world.setCollisionOverride(cell.x, cell.y, cell.collision);
    }
    // Terrain first, then flights (setTransition writes in the same order), then
    // drop any step that no longer climbs exactly one level from land — the
    // invariant _clearInvalidTransitionsAround keeps at runtime.
    for (const cell of transitionCells) world._setTransitionValue(cell.x, cell.y, cell.transition);
    for (const cell of transitionCells) {
      const transition = world.getTransition(cell.x, cell.y);
      if (!transition) continue;
      const [vx, vy] = MASK_VECTOR[transition.mask];
      if (world.getLayer(cell.x, cell.y, 'foundation')
        && world.getElevation(cell.x + vx, cell.y + vy) === world.getElevation(cell.x, cell.y) + 1) continue;
      world._setTransitionValue(cell.x, cell.y, 0);
      removedCells++;
    }

    const removedCreatures = world.loadCreatures(data.creatures);
    const removedNpcs = world.loadNpcs(data.npcs);
    return { world, removedTiles, removedCells, removedCreatures, removedNpcs };
  }

  /**
   * An animal dropped where its foot box is blocked (open water, a wall, the
   * void) could never move again; refuse the placement instead.
   */
  canPlaceCreature(x, y) {
    return Number.isFinite(x) && Number.isFinite(y) && !isFootBlocked(this, x, y);
  }

  placeCreature(species, x, y) {
    if (!isSpecies(species) || !this.canPlaceCreature(x, y)) return null;
    const creature = new Animal({
      id: this.nextCreatureId,
      species,
      spawnX: x,
      spawnY: y,
      x,
      y,
      facing: 'right',
    });
    this.nextCreatureId += 1;
    this.creatures.push(creature);
    return creature;
  }

  hasCreatureAt(tileX, tileY) {
    return this.creatures.some((creature) => Math.floor(creature.x) === tileX
      && Math.floor(creature.y) === tileY)
      || this.hasNpcAt(tileX, tileY);
  }

  eraseCreaturesAt(tileX, tileY) {
    const remaining = this.creatures.filter((creature) => Math.floor(creature.x) !== tileX
      || Math.floor(creature.y) !== tileY);
    const removed = this.creatures.length - remaining.length;
    this.creatures = remaining;
    return removed + this.eraseNpcsAt(tileX, tileY);
  }

  loadCreatures(records) {
    let removed = 0;
    for (const record of records ?? []) {
      if (!this._restoreCreature(record)) removed += 1;
    }
    return removed;
  }

  _restoreCreature(record) {
    if (!record || !isSpecies(record.species) || !Number.isInteger(record.id) || record.id < 1
      || !Number.isFinite(record.spawnX) || !Number.isFinite(record.spawnY)
      || !Number.isFinite(record.x) || !Number.isFinite(record.y)
      || (record.facing !== 'left' && record.facing !== 'right')) {
      return false;
    }
    this.creatures.push(Animal.fromJSON(record));
    this.nextCreatureId = Math.max(this.nextCreatureId, record.id + 1);
    return true;
  }

  placeNpc(npcId, x, y) {
    if (!Number.isInteger(npcId) || npcId < 1 || !Number.isFinite(x) || !Number.isFinite(y)) return null;
    if (this.isNpcPlaced(npcId)) return null;
    // NPCs collide, so one dropped on water, a wall, or the void would be stuck.
    if (isFootBlocked(this, x, y)) return null;
    const npc = new Npc({ npcId, spawnX: x, spawnY: y, x, y, direction: 'down' });
    this.npcs.push(npc);
    return npc;
  }

  isNpcPlaced(npcId) {
    return this.npcs.some((npc) => npc.npcId === npcId);
  }

  hasNpcAt(tileX, tileY) {
    return this.npcs.some((npc) => Math.floor(npc.x) === tileX && Math.floor(npc.y) === tileY);
  }

  eraseNpcsAt(tileX, tileY) {
    const remaining = this.npcs.filter((npc) => Math.floor(npc.x) !== tileX
      || Math.floor(npc.y) !== tileY);
    const removed = this.npcs.length - remaining.length;
    this.npcs = remaining;
    return removed;
  }

  /** Un-place a specific NPC, wherever it stands. Used when its definition is deleted. */
  removeNpc(npcId) {
    const remaining = this.npcs.filter((npc) => npc.npcId !== npcId);
    const removed = this.npcs.length - remaining.length;
    this.npcs = remaining;
    return removed;
  }

  loadNpcs(records) {
    let removed = 0;
    for (const record of records ?? []) {
      if (!this._restoreNpc(record)) removed += 1;
    }
    return removed;
  }

  _restoreNpc(record) {
    if (!record || !Number.isInteger(record.npcId) || record.npcId < 1
      || !Number.isFinite(record.spawnX) || !Number.isFinite(record.spawnY)
      || !Number.isFinite(record.x) || !Number.isFinite(record.y)
      || !NPC_DIRECTIONS.includes(record.direction)
      || this.isNpcPlaced(record.npcId)) {
      return false;
    }
    this.npcs.push(Npc.fromJSON(record));
    return true;
  }

  /** Setter-path address; the read-only getters below inline this to stay allocation-free. */
  _getChunk(x, y, create = false) {
    const chunkX = Math.floor(x / CHUNK_SIZE);
    const chunkY = Math.floor(y / CHUNK_SIZE);
    const key = chunkKeyFor(chunkX, chunkY);
    let chunk = this.chunks.get(key);
    if (!chunk && create) {
      chunk = new Chunk();
      this.chunks.set(key, chunk);
    }
    return { key, index: (y - chunkY * CHUNK_SIZE) * CHUNK_SIZE + (x - chunkX * CHUNK_SIZE), chunk };
  }

  getLayer(x, y, layer) {
    const chunkX = Math.floor(x / CHUNK_SIZE);
    const chunkY = Math.floor(y / CHUNK_SIZE);
    const chunk = this.chunks.get(chunkKeyFor(chunkX, chunkY));
    if (!chunk) return 0;
    return chunk.layers[layer]?.[(y - chunkY * CHUNK_SIZE) * CHUNK_SIZE + (x - chunkX * CHUNK_SIZE)] ?? 0;
  }

  getPlacement(x, y, layer) {
    const chunkX = Math.floor(x / CHUNK_SIZE);
    const chunkY = Math.floor(y / CHUNK_SIZE);
    const chunk = this.chunks.get(chunkKeyFor(chunkX, chunkY));
    if (!chunk) return 0;
    return chunk.placements[layer]?.[(y - chunkY * CHUNK_SIZE) * CHUNK_SIZE + (x - chunkX * CHUNK_SIZE)] ?? 0;
  }

  getElevation(x, y) {
    const chunkX = Math.floor(x / CHUNK_SIZE);
    const chunkY = Math.floor(y / CHUNK_SIZE);
    const chunk = this.chunks.get(chunkKeyFor(chunkX, chunkY));
    if (!chunk) return 0;
    return chunk.elevation[(y - chunkY * CHUNK_SIZE) * CHUNK_SIZE + (x - chunkX * CHUNK_SIZE)];
  }

  setElevation(x, y, elevation) {
    if (!Number.isInteger(elevation) || elevation < 0 || elevation > 3) {
      throw new Error(`Invalid elevation: ${elevation}`);
    }
    if (elevation > 0 && !this.getLayer(x, y, 'foundation')) return false;
    const address = this._getChunk(x, y, elevation > 0);
    if (!address.chunk) return false;
    const previous = address.chunk.elevation[address.index];
    if (previous === elevation) return false;
    if (previous === 0 && elevation > 0) address.chunk.elevatedCells++;
    if (previous > 0 && elevation === 0) address.chunk.elevatedCells--;
    address.chunk.elevation[address.index] = elevation;
    this._clearInvalidTransitionsAround(x, y);
    if (address.chunk.empty) this.chunks.delete(address.key);
    return true;
  }

  canSetElevation(x, y, elevation) {
    return Number.isInteger(elevation) && elevation >= 0 && elevation <= 3
      && Boolean(this.getLayer(x, y, 'foundation'))
      && this.getElevation(x, y) !== elevation;
  }

  getTransition(x, y) {
    const chunkX = Math.floor(x / CHUNK_SIZE);
    const chunkY = Math.floor(y / CHUNK_SIZE);
    const chunk = this.chunks.get(chunkKeyFor(chunkX, chunkY));
    const encoded = chunk
      ? chunk.transitions[(y - chunkY * CHUNK_SIZE) * CHUNK_SIZE + (x - chunkX * CHUNK_SIZE)]
      : 0;
    if (!encoded) return null;
    const directions = [1, 2, 4, 8];
    return {
      kind: encoded > 4 ? 'stairs' : 'ramp',
      mask: directions[(encoded - 1) % 4],
    };
  }

  _setTransitionValue(x, y, value) {
    const address = this._getChunk(x, y, value > 0);
    if (!address.chunk) return;
    const previous = address.chunk.transitions[address.index];
    if (!previous && value) address.chunk.transitionCells++;
    if (previous && !value) address.chunk.transitionCells--;
    address.chunk.transitions[address.index] = value;
    if (address.chunk.empty) this.chunks.delete(address.key);
  }

  setTransition(x, y, kind) {
    if (!['ramp', 'stairs'].includes(kind)) throw new Error(`Invalid transition: ${kind}`);
    const plan = this._planTransition(x, y, kind);
    if (!plan) return false;
    const value = (kind === 'stairs' ? 4 : 0) + [1, 2, 4, 8].indexOf(plan.mask) + 1;
    // Terrain first, then the flight itself: carving a step clears transitions
    // around the cell it touches, which would wipe steps written before it.
    for (const step of plan.steps) {
      if (this.getElevation(step.x, step.y) !== step.elevation) this.setElevation(step.x, step.y, step.elevation);
    }
    for (const step of plan.steps) this._setTransitionValue(step.x, step.y, value);
    return true;
  }

  canSetTransition(x, y, kind) {
    if (!['ramp', 'stairs'].includes(kind)) return false;
    return Boolean(this._planTransition(x, y, kind));
  }

  _planTransition(x, y, kind) {
    const here = this.getElevation(x, y);
    let best = null;
    for (const direction of TRANSITION_DIRECTIONS) {
      const neighbor = this.getElevation(x + direction.dx, y + direction.dy);
      if (neighbor === here) continue;
      const climbs = neighbor > here;
      const base = climbs ? { x, y } : { x: x + direction.dx, y: y + direction.dy };
      const plan = this._planFlight(
        base.x, base.y, climbs ? direction.mask : OPPOSITE_MASK[direction.mask], kind,
      );
      // An already-carved flight leaves nothing to do; another direction may
      // still have a climb worth cutting.
      if (!plan || plan.complete) continue;
      if (!best || plan.steps.length < best.steps.length) best = plan;
    }
    return best;
  }

  /**
   * Plans a whole flight from the base cell to the top of the cliff `mask`
   * points at. A one-level ledge yields a single step; taller cliffs carve the
   * plateau back into a stepped notch, one cell per level. Null when none fits.
   */
  _planFlight(baseX, baseY, mask, kind) {
    const [dx, dy] = MASK_VECTOR[mask];
    const low = this.getElevation(baseX, baseY);
    if (!this.getLayer(baseX, baseY, 'foundation') || !this._isClimbableFrom(baseX, baseY)) return null;
    const top = this.getElevation(baseX + dx, baseY + dy);
    const climb = top - low;
    if (climb < 1) return null;
    // The plateau has to run one cell deeper than the climb is tall, so the last
    // step still has somewhere to arrive.
    for (let depth = 1; depth <= climb; depth++) {
      const cx = baseX + dx * depth; const cy = baseY + dy * depth;
      if (this.getElevation(cx, cy) !== top || !this.getLayer(cx, cy, 'foundation')) return null;
    }
    const steps = [{ x: baseX, y: baseY, elevation: low }];
    for (let level = 1; level < climb; level++) {
      const cx = baseX + dx * level; const cy = baseY + dy * level;
      if (STRUCTURAL_CHANNELS.some((channel) => this.getPlacement(cx, cy, channel))) return null;
      steps.push({ x: cx, y: cy, elevation: low + level });
    }
    // A cell holds one transition. Swapping a ramp for stairs along the same
    // boundary is fine; re-aiming a cell would silently break its own flight.
    if (steps.some((step) => {
      const existing = this.getTransition(step.x, step.y);
      return existing && existing.mask !== mask;
    })) return null;
    // Connected flights are at most 3 cells wide.
    if (this._flightWidth(baseX, baseY, mask, kind) > 3) return null;
    const complete = steps.every((step) => {
      const existing = this.getTransition(step.x, step.y);
      return this.getElevation(step.x, step.y) === step.elevation
        && existing?.kind === kind && existing.mask === mask;
    });
    return { mask, steps, complete };
  }

  /**
   * A flight has to start somewhere a walker can already stand: open ground, or
   * a terrace another ramp or stairway already opens onto. Without this a step
   * can be cut into an upper cliff band with no way to reach it.
   */
  _isClimbableFrom(x, y) {
    const elevation = this.getElevation(x, y);
    if (elevation === 0) return true;
    return TRANSITION_DIRECTIONS.some((direction) => {
      const nx = x + direction.dx; const ny = y + direction.dy;
      if (this.getElevation(nx, ny) !== elevation - 1) return false;
      const below = this.getTransition(nx, ny);
      if (!below) return false;
      const [vx, vy] = MASK_VECTOR[below.mask];
      return nx + vx === x && ny + vy === y;
    });
  }

  /** How wide a same-kind, same-mask flight would be if this cell joined it. Max 3. */
  _flightWidth(x, y, mask, kind) {
    const parallel = mask === 1 || mask === 4 ? [[-1, 0], [1, 0]] : [[0, -1], [0, 1]];
    let width = 1;
    for (const [px, py] of parallel) {
      for (let step = 1; step <= 3; step++) {
        const adjacent = this.getTransition(x + px * step, y + py * step);
        if (adjacent?.kind !== kind || adjacent.mask !== mask) break;
        width++;
      }
    }
    return width;
  }

  clearTransitionAt(x, y) {
    if (!this.getTransition(x, y)) return false;
    this._setTransitionValue(x, y, 0);
    return true;
  }

  _clearInvalidTransitionsAround(x, y) {
    const offsets = [[0,0],[0,-1],[1,0],[0,1],[-1,0]];
    for (const [dx, dy] of offsets) {
      const tx = x + dx; const ty = y + dy;
      const transition = this.getTransition(tx, ty);
      if (!transition) continue;
      const [vx, vy] = MASK_VECTOR[transition.mask];
      if (this.getElevation(tx + vx, ty + vy) !== this.getElevation(tx, ty) + 1) this._setTransitionValue(tx, ty, 0);
    }
  }

  placeTile(x, y, tileId) {
    const tile = this.tileCatalog.get(tileId);
    ({ x, y } = this.resolveTilePlacement(x, y, tileId));
    if (!this.canPlaceTile(x, y, tileId)) return false;
    const layers = tile.occupies;
    const footprint = tile.footprint ?? { width: 1, height: 1 };
    const cells = [];
    for (let dy = 0; dy < footprint.height; dy++) for (let dx = 0; dx < footprint.width; dx++) {
      cells.push({ x: x + dx, y: y - dy });
    }
    const conflicts = new Set(cells.flatMap((cell) =>
      layers.map((layer) => this.getPlacement(cell.x, cell.y, layer))).filter(Boolean));
    for (const placementId of conflicts) this.erasePlacement(placementId);

    const placementId = this.nextPlacementId++;
    for (const cell of cells) {
      const { chunk, index } = this._getChunk(cell.x, cell.y, true);
      for (const layer of layers) {
        chunk.layers[layer][index] = tileId;
        chunk.placements[layer][index] = placementId;
        chunk.occupiedLayerCells++;
      }
    }
    this.placementIndex.set(placementId, { x, y, layers, cells });
    return true;
  }

  canPlaceTile(x, y, tileId) {
    const tile = this.tileCatalog.get(tileId);
    if (!tile || tile.channel === 'backdrop' || !Array.isArray(tile.occupies)
      || tile.occupies.length === 0 || tile.occupies.some((layer) => !MAP_CHANNELS.includes(layer))) return false;
    ({ x, y } = this.resolveTilePlacement(x, y, tileId));
    if (tile.channel !== 'foundation' && !tile.waterObject && !this.getLayer(x, y, 'foundation')) return false;
    // Outdoor paths belong on open land, not under building footprint shells.
    if (tile.channel === 'outdoorFloor' && this.tileCatalog.get(this.getLayer(x, y, 'exterior'))?.shell) return false;
    // Interior floors go on the room interior of a footprint, not the outer wall ring.
    // (Edge shell cells are walkable walls; doors open that ring via opensWall.)
    if (tile.channel === 'interiorFloor' && this.isExteriorBoundary(x, y)) return false;
    if (tile.paletteSection === 'interiorWalls' && !this.isRoomBackWall(x, y)) return false;
    if (tile.paletteSection === 'interiorWallObjects' && !this.isRoomBackWall(x, y)) return false;
    if (tile.channel === 'roof' && !this.tileCatalog.get(this.getLayer(x, y, 'exterior'))?.shell) return false;
    const footprint = tile.footprint ?? { width: 1, height: 1 };
    for (let dy = 0; dy < footprint.height; dy++) for (let dx = 0; dx < footprint.width; dx++) {
      const cellX = x + dx; const cellY = y - dy;
      if (tile.channel !== 'foundation' && !tile.waterObject && !this.getLayer(cellX, cellY, 'foundation')) return false;
    }
    return true;
  }

  fillBuildingRoof(x, y, tileId) {
    const roof = this.tileCatalog.get(tileId);
    const exterior = this.tileCatalog.get(this.getLayer(x, y, 'exterior'));
    if (roof?.channel !== 'roof' || !exterior?.shell) return false;
    const visited = new Set();
    const pending = [[x, y]];
    let changed = false;
    while (pending.length) {
      const [cellX, cellY] = pending.pop();
      const key = `${cellX},${cellY}`;
      if (visited.has(key)) continue;
      visited.add(key);
      const cellExterior = this.tileCatalog.get(this.getLayer(cellX, cellY, 'exterior'));
      if (!cellExterior?.shell) continue;
      if (this.getLayer(cellX, cellY, 'roof') !== tileId) {
        changed = this.placeTile(cellX, cellY, tileId) || changed;
      }
      for (const [dx, dy] of [[0,-1],[1,0],[0,1],[-1,0]]) pending.push([cellX + dx, cellY + dy]);
    }
    return changed;
  }

  fillRoomBackWall(x, y, tileId) {
    const wall = this.tileCatalog.get(tileId);
    ({ x, y } = this.resolveTilePlacement(x, y, tileId));
    if (wall?.paletteSection !== 'interiorWalls' || !this.isRoomBackWall(x, y)) return false;
    const exterior = this.tileCatalog.get(this.getLayer(x, y, 'exterior'));
    let changed = false;
    for (let cellX = x; this._sameExteriorGroup(cellX, y, exterior); cellX--) {
      if (this.isRoomBackWall(cellX, y) && this.getLayer(cellX, y, 'wall') !== tileId) changed = this.placeTile(cellX, y, tileId) || changed;
    }
    for (let cellX = x + 1; this._sameExteriorGroup(cellX, y, exterior); cellX++) {
      if (this.isRoomBackWall(cellX, y) && this.getLayer(cellX, y, 'wall') !== tileId) changed = this.placeTile(cellX, y, tileId) || changed;
    }
    return changed;
  }

  _sameExteriorGroup(x, y, exterior) {
    const candidate = this.tileCatalog.get(this.getLayer(x, y, 'exterior'));
    return Boolean(candidate?.shell && (candidate.id === exterior?.id
      || (candidate.connectionGroup && candidate.connectionGroup === exterior?.connectionGroup)));
  }

  /**
   * The back-wall plane spans the room *between* the side walls. Including the
   * ring's corner cells would cut the side wall bands off at the top, so a
   * corner is not a back wall even though its north side is open.
   */
  isRoomBackWall(x, y) {
    const exterior = this.tileCatalog.get(this.getLayer(x, y, 'exterior'));
    return Boolean(exterior?.shell && !this._sameExteriorGroup(x, y - 1, exterior)
      && this._sameExteriorGroup(x - 1, y, exterior)
      && this._sameExteriorGroup(x + 1, y, exterior));
  }

  resolveTilePlacement(x, y, tileId) {
    const tile = this.tileCatalog.get(tileId);
    if (['interiorWalls', 'interiorWallObjects'].includes(tile?.paletteSection)
      && !this.isRoomBackWall(x, y) && this.isRoomBackWall(x, y - 1)) return { x, y: y - 1 };
    if (tile?.paletteSection !== 'exteriorObjects' || this.getLayer(x, y, 'exterior')) return { x, y };
    const northExterior = this.tileCatalog.get(this.getLayer(x, y - 1, 'exterior'));
    const exteriorHere = this.tileCatalog.get(this.getLayer(x, y, 'exterior'));
    if (northExterior?.shell && northExterior.components?.onGround
      && (!exteriorHere || exteriorHere.connectionGroup !== northExterior.connectionGroup)) return { x, y: y - 1 };
    return { x, y };
  }

  isTreeBase(x, y) {
    const wood = this.tileCatalog.get(this.getLayer(x, y, 'treeWood'));
    return wood?.treePart === 'static';
  }

  eraseAt(x, y) {
    const placements = new Set(MAP_CHANNELS.map((layer) => this.getPlacement(x, y, layer)).filter(Boolean));
    for (const placementId of placements) this.erasePlacement(placementId);
    return placements.size > 0;
  }

  eraseTerrainAt(x, y) {
    const placements = new Set(['outdoorFloor', 'surface', 'foundation']
      .map((channel) => this.getPlacement(x, y, channel)).filter(Boolean));
    for (const placementId of placements) this.erasePlacement(placementId);
    return placements.size > 0;
  }

  eraseObjectsAt(x, y) {
    const placements = new Set(['onGround', 'treeWood']
      .map((channel) => this.getPlacement(x, y, channel)).filter(Boolean));
    for (const placementId of placements) this.erasePlacement(placementId);
    return placements.size > 0;
  }

  eraseChannelAt(x, y, channel) {
    const placementId = this.getPlacement(x, y, channel);
    if (placementId) this.erasePlacement(placementId);
    if (channel === 'foundation') {
      for (const dependentChannel of MAP_CHANNELS) {
        if (dependentChannel === 'foundation') continue;
        const dependentTile = this.tileCatalog.get(this.getLayer(x, y, dependentChannel));
        if (dependentTile?.waterObject) continue;
        const dependentPlacement = this.getPlacement(x, y, dependentChannel);
        if (dependentPlacement) this.erasePlacement(dependentPlacement);
      }
      this.setElevation(x, y, 0);
      this.clearTransitionAt(x, y);
    }
    if (channel === 'exterior') {
      const roofPlacement = this.getPlacement(x, y, 'roof');
      if (roofPlacement) this.erasePlacement(roofPlacement);
    }
    return Boolean(placementId);
  }

  erasePlacement(placementId) {
    const placement = this.placementIndex.get(placementId);
    if (!placement) return false;
    for (const cell of placement.cells ?? [{ x: placement.x, y: placement.y }]) {
      const { chunk, index, key } = this._getChunk(cell.x, cell.y);
      if (!chunk) continue;
      for (const layer of placement.layers) {
        if (chunk.placements[layer][index] !== placementId) continue;
        chunk.layers[layer][index] = 0;
        chunk.placements[layer][index] = 0;
        chunk.occupiedLayerCells--;
      }
      if (chunk.empty) this.chunks.delete(key);
    }
    this.placementIndex.delete(placementId);
    return true;
  }

  getPlacementRecord(placementId) {
    return this.placementIndex.get(placementId) ?? null;
  }

  /** state: -1 inherited, 0 forced passable, 1 forced blocked */
  setCollisionOverride(x, y, state) {
    if (![-1, 0, 1].includes(state)) throw new Error(`Invalid collision override: ${state}`);
    let address = this._getChunk(x, y, state !== -1);
    if (!address.chunk) return;
    const previous = address.chunk.collision[address.index];
    if (previous === -1 && state !== -1) address.chunk.collisionOverrides++;
    if (previous !== -1 && state === -1) address.chunk.collisionOverrides--;
    address.chunk.collision[address.index] = state;
    if (address.chunk.empty) this.chunks.delete(address.key);
  }

  getCollisionOverride(x, y) {
    const chunkX = Math.floor(x / CHUNK_SIZE);
    const chunkY = Math.floor(y / CHUNK_SIZE);
    const chunk = this.chunks.get(chunkKeyFor(chunkX, chunkY));
    if (!chunk) return -1;
    return chunk.collision[(y - chunkY * CHUNK_SIZE) * CHUNK_SIZE + (x - chunkX * CHUNK_SIZE)];
  }

  isExteriorBoundary(x, y) {
    const exterior = this.tileCatalog.get(this.getLayer(x, y, 'exterior'));
    const wallObject = this.tileCatalog.get(this.getLayer(x, y, 'wallObject'));
    if (!exterior?.shell || wallObject?.opensWall) return false;
    return [[0,-1],[1,0],[0,1],[-1,0]]
      .some(([dx, dy]) => this.getLayer(x + dx, y + dy, 'exterior') !== exterior.id);
  }

  /**
   * South facades are drawn one cell south of a free south edge of the shell
   * (renderer._drawExteriorFacade). That projected cell must block movement so
   * the player cannot walk onto the wall face. Doors leave their approach open.
   */
  isProjectedSouthFacade(x, y) {
    const north = this.tileCatalog.get(this.getLayer(x, y - 1, 'exterior'));
    if (!north?.shell || !north.components?.onGround) return false;
    const here = this.tileCatalog.get(this.getLayer(x, y, 'exterior'));
    // Same shell group continuing south means this cell is still footprint, not
    // a free face. Prefer connectionGroup (matches the renderer); fall back to
    // id when groups are unset (test catalogs / plain shells).
    const sameGroup = Boolean(here?.shell && (
      north.connectionGroup != null
        ? here.connectionGroup === north.connectionGroup
        : here.id === north.id
    ));
    if (sameGroup) return false;
    if (this.tileCatalog.get(this.getLayer(x, y - 1, 'wallObject'))?.opensWall) return false;
    return true;
  }

  isCollidable(x, y) {
    const override = this.getCollisionOverride(x, y);
    if (override !== -1) return override === 1;
    const foundation = this.tileCatalog.get(this.getLayer(x, y, 'foundation'));
    // Empty foundation cells render as open water. The coastline is drawn inside
    // the outermost foundation tile, so that tile remains available for wading
    // while the water immediately beyond it blocks movement.
    if (!foundation) return true;
    if (this.isProjectedCliffFace(x, y)) return true;
    if (this.isProjectedSouthFacade(x, y)) return true;
    const surface = this.tileCatalog.get(this.getLayer(x, y, 'surface'));
    const wall = this.tileCatalog.get(this.getLayer(x, y, 'wall'));
    const wallObject = this.tileCatalog.get(this.getLayer(x, y, 'wallObject'));
    const object = this.tileCatalog.get(this.getLayer(x, y, 'onGround'));
    const treeWood = this.tileCatalog.get(this.getLayer(x, y, 'treeWood'));
    if (wallObject?.opensWall) return false;
    if (foundation?.collision.ground || surface?.collision.ground || object?.collision.ground
      || (treeWood?.collision.ground && this.isTreeBase(x, y))
      || wall?.collision.wall || wallObject?.collision.wall || this.isExteriorBoundary(x, y)) return true;
    return false;
  }

  isProjectedCliffFace(x, y) {
    // South-facing cliffs are rendered downward over one cell per elevation
    // band. Those projected cells must occupy the same footprint visually and
    // physically. A valid ground-to-level-one transition replaces its band.
    for (let band = 1; band <= 3; band++) {
      const sourceY = y - band;
      const drop = this.getElevation(x, sourceY) - this.getElevation(x, sourceY + 1);
      if (drop < band) continue;
      const transition = this.getTransition(x, y);
      if (band === 1 && drop === 1 && transition?.mask === 1) continue;
      return true;
    }
    return false;
  }

  isMovementBlocked(fromX, fromY, toX, toY) {
    if (this.isCollidable(toX, toY)) return true;
    if (fromX === toX && fromY === toY) return false;
    if (this.getCollisionOverride(toX, toY) === 0) return false;

    const fromElevation = this.getElevation(fromX, fromY);
    const toElevation = this.getElevation(toX, toY);
    if (fromElevation === toElevation) return false;
    if (Math.abs(fromElevation - toElevation) !== 1) return true;

    const lower = fromElevation < toElevation
      ? { x: fromX, y: fromY, highX: toX, highY: toY }
      : { x: toX, y: toY, highX: fromX, highY: fromY };
    const transition = this.getTransition(lower.x, lower.y);
    if (!transition) return true;
    const [dx, dy] = MASK_VECTOR[transition.mask];
    return lower.x + dx !== lower.highX || lower.y + dy !== lower.highY;
  }
}
